import React from 'react';
import Panel from '../../components/ui/Panel';
import CodeBlock from '../../components/ui/CodeBlock';

const SpacingPage: React.FC = () => {
  const tailwindConfigCode = `// tailwind.config.js
module.exports = {
  theme: {
    extend: {
      spacing: {
        // We use the standard Tailwind spacing scale
        // but can extend it for specific OSRS needs
        'osrs-xs': '0.25rem', // 4px
        'osrs-sm': '0.5rem',  // 8px
        'osrs-md': '1rem',    // 16px
        'osrs-lg': '1.5rem',  // 24px
        'osrs-xl': '2rem',    // 32px
        'osrs-2xl': '3rem',   // 48px
      },
    },
  },
}`;

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Spacing</h1>
      <p className="text-osrs-yellow mb-8">
        Consistent spacing is essential for creating a balanced and readable interface that honors
        the OSRS aesthetic while ensuring good usability.
      </p>
      
      <section id="scale" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Spacing Scale</h2>
        <p className="text-osrs-yellow mb-6">
          The OSRS Design System uses a consistent spacing scale based on 4px units (0.25rem).
          This scale helps maintain vertical rhythm and consistent component spacing.
        </p>
        
        <div className="space-y-8 mb-8">
          <Panel className="mb-6">
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">Spacing Units</h3>
            <div className="space-y-6">
              <div className="flex items-center">
                <div className="w-32">
                  <p className="text-osrs-gold font-bold">4px (0.25rem)</p>
                  <p className="text-osrs-yellow text-sm">osrs-xs</p>
                </div>
                <div className="h-6 w-1 bg-osrs-gold"></div>
              </div>
              
              <div className="flex items-center">
                <div className="w-32">
                  <p className="text-osrs-gold font-bold">8px (0.5rem)</p>
                  <p className="text-osrs-yellow text-sm">osrs-sm</p>
                </div>
                <div className="h-6 w-2 bg-osrs-gold"></div>
              </div>
              
              <div className="flex items-center">
                <div className="w-32">
                  <p className="text-osrs-gold font-bold">16px (1rem)</p>
                  <p className="text-osrs-yellow text-sm">osrs-md</p>
                </div>
                <div className="h-6 w-4 bg-osrs-gold"></div>
              </div>
              
              <div className="flex items-center">
                <div className="w-32">
                  <p className="text-osrs-gold font-bold">24px (1.5rem)</p>
                  <p className="text-osrs-yellow text-sm">osrs-lg</p>
                </div>
                <div className="h-6 w-6 bg-osrs-gold"></div>
              </div>
              
              <div className="flex items-center">
                <div className="w-32">
                  <p className="text-osrs-gold font-bold">32px (2rem)</p>
                  <p className="text-osrs-yellow text-sm">osrs-xl</p>
                </div>
                <div className="h-6 w-8 bg-osrs-gold"></div>
              </div>
              
              <div className="flex items-center">
                <div className="w-32">
                  <p className="text-osrs-gold font-bold">48px (3rem)</p>
                  <p className="text-osrs-yellow text-sm">osrs-2xl</p>
                </div>
                <div className="h-6 w-12 bg-osrs-gold"></div>
              </div>
            </div>
          </Panel>
          
          <Panel className="mb-6">
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">Common Applications</h3>
            <table className="w-full text-osrs-yellow">
              <thead>
                <tr className="border-b border-osrs-border">
                  <th className="text-left py-2 px-4 text-osrs-gold">Element</th>
                  <th className="text-left py-2 px-4 text-osrs-gold">Spacing</th>
                  <th className="text-left py-2 px-4 text-osrs-gold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-osrs-border">
                  <td className="py-2 px-4">Component padding</td>
                  <td className="py-2 px-4">16px (1rem)</td>
                  <td className="py-2 px-4">Default padding for panels and containers</td>
                </tr>
                <tr className="border-b border-osrs-border">
                  <td className="py-2 px-4">Margin between components</td>
                  <td className="py-2 px-4">24px (1.5rem)</td>
                  <td className="py-2 px-4">Space between major UI elements</td>
                </tr>
                <tr className="border-b border-osrs-border">
                  <td className="py-2 px-4">Button padding</td>
                  <td className="py-2 px-4">8px/16px (0.5rem/1rem)</td>
                  <td className="py-2 px-4">Vertical/horizontal padding for buttons</td>
                </tr>
                <tr className="border-b border-osrs-border">
                  <td className="py-2 px-4">Form elements spacing</td>
                  <td className="py-2 px-4">16px (1rem)</td>
                  <td className="py-2 px-4">Space between form inputs</td>
                </tr>
                <tr>
                  <td className="py-2 px-4">Section margins</td>
                  <td className="py-2 px-4">48px (3rem)</td>
                  <td className="py-2 px-4">Space between major page sections</td>
                </tr>
              </tbody>
            </table>
          </Panel>
        </div>
      </section>
      
      <section id="implementation" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Implementation</h2>
        
        <Panel title="Tailwind Config" className="mb-6">
          <p className="text-osrs-yellow mb-4">
            Extend Tailwind's spacing scale for OSRS-specific values:
          </p>
          <CodeBlock code={tailwindConfigCode} language="jsx" />
        </Panel>
      </section>
      
      <section id="usage" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Usage Guidelines</h2>
        
        <Panel className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">Consistency</h3>
          <p className="text-osrs-yellow mb-2">
            Follow these principles for consistent spacing:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use the spacing scale for all margins and padding</li>
            <li>Apply consistent spacing between similar elements</li>
            <li>Use larger spacing to separate distinct sections</li>
            <li>Use smaller spacing for related elements within a component</li>
            <li>Maintain a 16px (1rem) padding minimum for content containers</li>
          </ul>
        </Panel>
        
        <Panel className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">OSRS Aesthetic</h3>
          <p className="text-osrs-yellow mb-2">
            While OSRS has a compact, pixel-art style, ensure enough breathing room:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Interface elements should not feel cramped</li>
            <li>Add more space around interactive elements like buttons</li>
            <li>Dense information areas (inventory, stats) can use tighter spacing</li>
            <li>Main content areas should have generous spacing for readability</li>
          </ul>
        </Panel>
      </section>
    </div>
  );
};

export default SpacingPage; 